import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  dismissGuide,
  getTrainingGuide,
  resolveStepTime,
  stepIndexForTime,
  trainingMediaSrc,
} from "../data/trainingGuides";
import "../assets/css/AdminTrainingGuide.css";

const TrainingGuideContext = createContext(null);

export function useTrainingGuide() {
  return useContext(TrainingGuideContext);
}

export function TrainingGuideProvider({ userId, children }) {
  const [guideId, setGuideId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("modal");
  const [stepIndex, setStepIndex] = useState(0);
  const guideIdRef = useRef(null);
  const stepIndexRef = useRef(0);

  const openGuide = useCallback((id) => {
    guideIdRef.current = id;
    stepIndexRef.current = 0;
    setGuideId(id);
    setStepIndex(0);
    setMode("modal");
    setIsOpen(true);
  }, []);

  const closeGuide = useCallback(
    (options = {}) => {
      if (options.dontShowAgain && guideIdRef.current) {
        dismissGuide(userId, guideIdRef.current);
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      setIsOpen(false);
      setMode("modal");
    },
    [userId],
  );

  const selectStep = useCallback((index) => {
    stepIndexRef.current = index;
    setStepIndex(index);
  }, []);

  const value = useMemo(
    () => ({
      guideId,
      guide: getTrainingGuide(guideId),
      isOpen,
      mode,
      stepIndex,
      stepIndexRef,
      openGuide,
      closeGuide,
      selectStep,
      setMode,
    }),
    [guideId, isOpen, mode, stepIndex, openGuide, closeGuide, selectStep],
  );

  return (
    <TrainingGuideContext.Provider value={value}>
      {children}
    </TrainingGuideContext.Provider>
  );
}

export function TrainingGuideShell({ userId, children }) {
  return (
    <TrainingGuideProvider userId={userId}>
      {children}
      <AdminTrainingPlayer />
    </TrainingGuideProvider>
  );
}

function formatClock(seconds) {
  if (!Number.isFinite(seconds)) return "00:00";
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  const remain = total % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remain).padStart(2, "0")}`;
}

function speakStep(step) {
  if (!step || typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(`${step.title}. ${step.body}`);
  utterance.rate = 1;
  window.speechSynthesis.speak(utterance);
}

function AdminTrainingPlayer() {
  const training = useTrainingGuide();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const stepsRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [spoken, setSpoken] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [clock, setClock] = useState({ current: 0, duration: 0 });

  const guide = training?.guide;
  const step = guide?.steps[training.stepIndex];

  useEffect(() => {
    if (!training?.isOpen) {
      setStarted(false);
      setSpoken(false);
      setDontShowAgain(false);
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    }
  }, [training?.isOpen, training?.guideId]);

  useEffect(() => {
    if (!training?.isOpen || training.mode !== "modal") return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [training?.isOpen, training?.mode]);

  useEffect(() => {
    if (!training?.isOpen || !spoken || !step || guide?.narrated) {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      return undefined;
    }
    speakStep(step);
    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [training?.isOpen, spoken, training?.stepIndex, step, guide?.narrated]);

  useEffect(() => {
    if (!training?.isOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") training.closeGuide({ dontShowAgain });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [training, dontShowAgain]);

  const seekToStep = useCallback(
    (index) => {
      const video = videoRef.current;
      const next = guide?.steps[index];
      if (!video || !next || !guide) return;
      const apply = () => {
        const duration = Number.isFinite(video.duration)
          ? video.duration
          : guide.durationSeconds;
        video.currentTime = resolveStepTime(next, guide.steps, duration);
      };
      if (video.readyState >= 1) apply();
      else {
        video.addEventListener("loadedmetadata", apply, { once: true });
        video.load();
      }
    },
    [guide],
  );

  useEffect(() => {
    if (!training?.isOpen || training.mode !== "modal" || !guide) return undefined;
    seekToStep(training.stepIndexRef.current);
    return undefined;
  }, [training?.isOpen, training?.mode, training?.guideId, guide, seekToStep, training?.stepIndexRef]);

  const onTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video || !guide) return;
    const duration = Number.isFinite(video.duration)
      ? video.duration
      : guide.durationSeconds;
    const nextIndex = stepIndexForTime(guide.steps, video.currentTime, duration);
    if (nextIndex !== training.stepIndexRef.current) {
      training.selectStep(nextIndex);
    }
    setClock((prev) => {
      const current = video.currentTime || 0;
      if (
        Math.floor(prev.current) === Math.floor(current) &&
        prev.duration === duration
      ) {
        return prev;
      }
      return { current, duration };
    });
  }, [guide, training]);

  useEffect(() => {
    if (!training?.isOpen || training.mode !== "modal") return undefined;
    const list = stepsRef.current;
    const current = list?.querySelector(".at-step.is-current");
    if (!list || !current) return undefined;
    const listTop = list.getBoundingClientRect().top;
    const listBottom = list.getBoundingClientRect().bottom;
    const itemTop = current.getBoundingClientRect().top;
    const itemBottom = current.getBoundingClientRect().bottom;
    if (itemTop < listTop) {
      list.scrollTop -= listTop - itemTop;
    } else if (itemBottom > listBottom) {
      list.scrollTop += itemBottom - listBottom;
    }
    return undefined;
  }, [training?.isOpen, training?.mode, training?.stepIndex]);

  const chooseStep = (index) => {
    training.selectStep(index);
    setStarted(true);
    seekToStep(index);
  };

  const runAction = (actionStep) => {
    const action = actionStep?.action;
    if (!action?.to) return;
    if (action.continueGuide && guide) {
      training.setMode("coach");
      const next = Math.min(training.stepIndex + 1, guide.steps.length - 1);
      training.selectStep(next);
      seekToStep(next);
    }
    navigate(action.to);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  if (!training?.isOpen || !guide || !step) return null;

  if (training.mode === "coach") {
    return createPortal(
      <aside className="at-coach" role="dialog" aria-label={guide.title}>
        <div className="at-coach-top">
          <span>
            Step {training.stepIndex + 1} of {guide.steps.length}
          </span>
          <button
            type="button"
            className="at-icon-btn"
            aria-label="Close training"
            onClick={() => training.closeGuide({ dontShowAgain })}
          >
            <i className="mdi mdi-close" />
          </button>
        </div>
        <h3>{step.title}</h3>
        {step.control ? <span className="at-control at-control-coach">{step.control}</span> : null}
        <p>{step.body}</p>
        {step.action ? (
          <div className="at-coach-actions">
            <button type="button" onClick={() => runAction(step)}>
              {step.action.label}
            </button>
          </div>
        ) : null}
        <div className="at-coach-nav">
          <button
            type="button"
            disabled={training.stepIndex === 0}
            onClick={() => chooseStep(training.stepIndex - 1)}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={training.stepIndex === guide.steps.length - 1}
            onClick={() => chooseStep(training.stepIndex + 1)}
          >
            Next
          </button>
          <button type="button" onClick={() => training.setMode("modal")}>
            Open Video
          </button>
        </div>
        {guide.autoOpenOn ? (
          <div className="at-coach-foot">
            <label className="at-dismiss">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(event) => setDontShowAgain(event.target.checked)}
              />
              Don't show this again
            </label>
          </div>
        ) : null}
      </aside>,
      document.body,
    );
  }

  const durationLabel = clock.duration
    ? formatClock(clock.duration)
    : guide.lengthLabel;

  return createPortal(
    <div
      className="at-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          training.closeGuide({ dontShowAgain });
        }
      }}
    >
      <div
        className="at-player"
        role="dialog"
        aria-modal="true"
        aria-label={guide.title}
      >
        <header className="at-player-bar">
          <div className="at-brand">
            <i className="mdi mdi-heart" aria-hidden="true" />
            <div>
              <strong>LoveStrong</strong>
              <span>Admin Training Center</span>
            </div>
          </div>
          <button
            type="button"
            className="at-icon-btn"
            aria-label="Close training"
            onClick={() => training.closeGuide({ dontShowAgain })}
          >
            <i className="mdi mdi-close" />
          </button>
        </header>
        <div className="at-player-body">
          <div className="at-video-col">
            <p className="at-video-label">Training Video</p>
            <div className={`at-stage${started ? " is-started" : ""}`}>
              <video
                ref={videoRef}
                src={trainingMediaSrc(guide.videoSrc)}
                poster={guide.thumbnail ? trainingMediaSrc(guide.thumbnail) : undefined}
                preload="none"
                playsInline
                controls={started}
                onTimeUpdate={onTimeUpdate}
                onPlay={() => setStarted(true)}
                onLoadedMetadata={onTimeUpdate}
              />
              {!started && guide.thumbnail ? (
                <img
                  className="at-stage-thumb"
                  src={trainingMediaSrc(guide.thumbnail)}
                  alt=""
                />
              ) : null}
              <button
                type="button"
                className={`at-play-overlay${guide.thumbnail ? " has-thumb" : ""}`}
                onClick={togglePlay}
              >
                <i className="mdi mdi-play" aria-hidden="true" />
                {guide.thumbnail ? null : <strong>{guide.title}</strong>}
                <span>
                  {formatClock(clock.current)} / {durationLabel}
                </span>
              </button>
            </div>
            {guide.definition && guide.narrated ? null : (
              <div className="at-video-tools">
                {guide.definition ? null : (
                  <p className="at-hint">Click a step to jump to its video timestamp.</p>
                )}
                {guide.narrated ? null : (
                  <button
                    type="button"
                    className={`at-text-btn${spoken ? " is-on" : ""}`}
                    onClick={() => {
                      setSpoken((current) => !current);
                      if (videoRef.current?.paused) {
                        videoRef.current.play().catch(() => {});
                      }
                    }}
                  >
                    {spoken ? "Spoken Guide On" : "Play Spoken Guide"}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="at-steps-col">
            <div className="at-steps-fit">
              {guide.definition ? (
                <aside className="at-define" aria-label={`${guide.title} definition`}>
                  <p className="at-define-kicker">Definition</p>
                  <h2>{guide.title}</h2>
                  <div className="at-define-card">
                    {(Array.isArray(guide.definition)
                      ? guide.definition
                      : [guide.definition]
                    ).map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </aside>
              ) : (
                <>
                  <div className="at-steps-head">
                    <h2>Follow the Steps</h2>
                    <span>
                      {training.stepIndex + 1} of {guide.steps.length}
                    </span>
                  </div>
                  <ol className="at-steps" ref={stepsRef}>
                  {guide.steps.map((item, index) => {
                    const state =
                      index < training.stepIndex
                        ? "is-done"
                        : index === training.stepIndex
                          ? "is-current"
                          : "";
                    return (
                      <li key={item.id} className={`at-step ${state}`} data-at-step={index}>
                        <button
                          type="button"
                          className="at-step-hit"
                          onClick={() => chooseStep(index)}
                        >
                          <span className="at-step-title">
                            <span className="at-step-mark" aria-hidden="true">
                              {index < training.stepIndex ? (
                                <i className="mdi mdi-check" />
                              ) : (
                                index + 1
                              )}
                            </span>
                            {item.title}
                          </span>
                          <span className="at-step-time">
                            {item.timeLabel}
                            {index === training.stepIndex ? " · Current Step" : ""}
                            {index < training.stepIndex ? " · Completed" : ""}
                          </span>
                        </button>
                        {index === training.stepIndex ? (
                          <div className="at-step-body">
                            {item.control ? (
                              <span className="at-control">{item.control}</span>
                            ) : null}
                            <p>{item.body}</p>
                            {item.action ? (
                              <button
                                type="button"
                                className="at-action"
                                onClick={() => runAction(item)}
                              >
                                {item.action.label}
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                  </ol>
                </>
              )}
            </div>
          </div>
        </div>
        <footer className="at-player-foot">
          {guide.autoOpenOn ? (
            <label className="at-dismiss">
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(event) => setDontShowAgain(event.target.checked)}
              />
              Don't show this again
            </label>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="at-ghost"
            onClick={() => training.closeGuide({ dontShowAgain })}
          >
            Close
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
